import { useEffect } from 'react';

import { Stage } from '@eventbrite/webapp-constants';

import { useBasicInfo } from '@/components/_shared/contexts/BasicInfoContext';
import { useIsPreview } from '@/components/_shared/contexts/PreviewContext';
import { useUserData } from '@/hooks/useUserData/useUserData';

import { isEventEnded } from '../../utils/bodyUtils';
import { useTrackingBeacons } from '../useTrackingBeacons/';

import { pixelsTracking } from './pixelsTracking';
import { seekaMergeProfile } from './seekaIdentify';

export const useTrackingPixels = ({ stage }: { stage: Stage }) => {
  const isPreview = useIsPreview();
  const { id: eventId, name: eventName, organizationId, organizer, isProtected, status } = useBasicInfo();
  const [trackingBeacons] = useTrackingBeacons();
  const { userId } = useUserData();

  useEffect(() => {
    if (isPreview || isProtected || isEventEnded(status)) {
      return;
    }
    if (trackingBeacons) {
      let environment;
      if (stage === Stage.PROD) {
        environment = 'prod';
      } else if (stage === Stage.TEST) {
        environment = 'qa';
      } else {
        environment = 'dev';
      }
      const debugging = stage !== Stage.PROD && process.env.NEXT_PUBLIC_TRACKING_PIXEL_DEBUG === 'true';

      pixelsTracking({
        eventId,
        eventName: eventName ?? '',
        organizationId,
        organizationName: organizer?.name ?? '',
        environment,
        debugging,
        trackingBeacons,
      });
    }
    // isPreview is stable for the component's lifetime; only trackingBeacons can change at runtime
  }, [trackingBeacons, isPreview]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seeka identity merge (Converge mergeProfile) on load for authenticated
  // users. Kept separate from the pixel pageview/track above so those fire
  // immediately on beacon load and aren't delayed by the user-data fetch. The
  // async profile fetch inside seekaMergeProfile also guarantees grylls.load()
  // (run above) has completed before seekaIdentify is called.
  useEffect(() => {
    if (isPreview || isProtected || isEventEnded(status)) {
      return;
    }
    if (trackingBeacons && userId) {
      // Cancel a stale, in-flight merge when the event/auth changes or the
      // component unmounts, so we never merge this event's profile into another
      // event's Seeka instances. status/isProtected are included so a transition
      // to ended/protected tears the in-flight merge down via cleanup.
      let cancelled = false;
      seekaMergeProfile({ eventId, trackingBeacons, isCancelled: () => cancelled });
      return () => {
        cancelled = true;
      };
    }
  }, [trackingBeacons, userId, isPreview, isProtected, status, eventId]);
};
